import React from "react";
import axios from "axios";

import { withStyles, makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@mui/material/CircularProgress";

import Constants from "./../../../Utils/Constants";
import LoadingProgress from "./../../../Common/LoadingProgress";
import { getHeaders } from "./../../../Utils/Common";
import store from "./../../../Common/storage";
import Header from "./../../../assets/components/header";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Pagination from '@material-ui/lab/Pagination';
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { useClipboard } from "use-clipboard-copy";
import toastMaker from "./../../../Common/toastmaker.js";
import jsPDF from 'jspdf';

import "./../../../index.css";

const StyledTableCell = withStyles(() => ({
  body: { fontSize: 13, fontWeight: 500, padding: '5px 10px' },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    "&:nth-of-type(even)": { backgroundColor: "rgba(251, 220, 119, 0.28)" },
  },
}))(TableRow);

const useStyles = makeStyles({ root: { width: "100%" } });

ChartJS.register(ArcElement, Tooltip, Legend);

const statuses = [
  'In Progress', 'Completed', 'Completed', 'Completed', 'Completed',
  'Paused', 'In Progress', 'In Progress', 'In Progress', 'Cancelled',
  'In Progress', 'Scheduled', 'Waiting for credits',
];

// ── Stat card component ────────────────────────────────────────────
const StatCard = ({ label, value, color, sub }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #E7E7ED',
    borderRadius: '8px',
    padding: '14px 18px',
    flex: '1',
    minWidth: '110px',
    textAlign: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  }}>
    <div style={{ fontSize: '24px', fontWeight: '700', color: color || '#122643', lineHeight: 1.2 }}>
      {value}
    </div>
    <div style={{ fontSize: '11px', color: '#6B6C7E', marginTop: '5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    {sub && <div style={{ fontSize: '10px', color: '#BABABA', marginTop: '2px' }}>{sub}</div>}
  </div>
);

export default function CampaignDetailReport() {
  const classes = useStyles();

  const [campaignData,  setCampaignData]  = React.useState(null);
  const [serverData,    setServerData]    = React.useState(null);
  const [page,          setPage]          = React.useState(1);
  const [pageCount,     setPageCount]     = React.useState(0);
  const [dialogOpen,    setDialogOpen]    = React.useState(false);
  const [dialogTitle,   setDialogTitle]   = React.useState('');
  const [dialogData,    setDialogData]    = React.useState('');
  const [linkClickCount, setLinkClickCount] = React.useState(0);
  const [usClickCount,  setUsClickCount]  = React.useState(0);
  const [mediaUrl,      setMediaUrl]      = React.useState('');
  const [aiInsights,    setAiInsights]    = React.useState('');
  const [aiLoading,     setAiLoading]     = React.useState(false);
  const [csvLoading,    setCsvLoading]    = React.useState(false);

  const id = window.location.pathname.split('/').pop();

  // ── Derived metrics ────────────────────────────────────────────────
  const total        = campaignData?.sp_number_of_contacts || 0;
  const failed       = campaignData?.failed_count || 0;
  const delivered    = total - failed;
  const deliveryRate = total > 0 ? ((delivered / total) * 100).toFixed(1) : '0.0';
  const clickRate    = delivered > 0 ? ((linkClickCount / delivered) * 100).toFixed(1) : '0.0';
  const unsubRate    = delivered > 0 ? ((usClickCount  / delivered) * 100).toFixed(1) : '0.0';

  React.useEffect(() => {
    axios
      .get(
        Constants.getApiPathWithConfigId(Constants.campaignhistoryApiPath) + `/${id}`,
        getHeaders()
      )
      .then(res => {
        setCampaignData(res.data.data);
        setLinkClickCount(res.data.data.link_clicks || 0);
        setUsClickCount(res.data.data.unsub_clicks || 0);
      })
      .catch(console.error);
  }, []);

  React.useEffect(() => {
    axios
      .get(
        Constants.getApiPathWithConfigId(Constants.campaignsmslogsApiPath) +
          `/${id}?per_page=10&page=${page}&order_by=id&order=desc`,
        getHeaders()
      )
      .then(res => {
        setPageCount(res.data.meta.last_page);
        setServerData(res.data.data);
      })
      .catch(console.error);
  }, [page]);

  // ── Pie chart data ─────────────────────────────────────────────────
  const chartData = {
    labels: ["Delivered", "Undeliverable"],
    datasets: [{
      label: "Delivery Report",
      data: [delivered, failed],
      backgroundColor: ["rgba(54, 162, 235, 0.6)", "rgba(255, 99, 132, 0.6)"],
      borderColor:     ["rgba(54, 162, 235, 1)",   "rgba(255, 99, 132, 1)"],
      borderWidth: 1,
    }],
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: "bottom" } },
  };

  // ── Personalization helper ─────────────────────────────────────────
  const getSms = r => {
    try {
      const tags = JSON.parse(r.personalization_tags);
      let m = r.sms;
      for (const p in tags) m = m.replace(p, tags[p]);
      return m;
    } catch {
      return r.sms;
    }
  };

  // ── Clipboard ─────────────────────────────────────────────────────
  const clipboard = useClipboard({
    onSuccess() {
      toastMaker.makeToast("Report copied to clipboard!", { variant: "success" });
    },
    onError() {
      toastMaker.makeToast("Failed to copy report.", { variant: "error" });
    },
  });

  // ── Export: PDF ───────────────────────────────────────────────────
  const downloadPDF = async () => {
    const doc = new jsPDF();

    // ── Header bar ──
    doc.setFillColor(18, 38, 67);
    doc.rect(0, 0, 210, 22, 'F');
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Campaign Performance Report', 105, 14, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    let y = 32;

    // ── Section header helper ──
    const sectionHeader = title => {
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(107, 108, 126);
      doc.text(title.toUpperCase(), 15, y);
      doc.setTextColor(0, 0, 0);
      y += 4;
      doc.setDrawColor(231, 231, 237);
      doc.line(15, y, 125, y);
      y += 5;
    };

    // ── Stat row helper (label left, value right) ──
    const statRow = (label, value) => {
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(107, 108, 126);
      doc.text(label, 20, y);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(39, 40, 51);
      doc.text(String(value ?? ''), 95, y);
      doc.setTextColor(0, 0, 0);
      y += 7;
    };

    sectionHeader('Campaign Details');
    statRow('Campaign',  campaignData?.name);
    statRow('Sender ID', campaignData?.sender_id);
    statRow('Date',      new Date(campaignData?.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }));
    statRow('List',      campaignData?.list_ac_names);
    statRow('Segment',   campaignData?.segment_ac_names);
    statRow('Status',    statuses[campaignData?.status]);

    y += 3;
    sectionHeader('Delivery');
    statRow('Total Sent',     total);
    statRow('Delivered',      `${delivered}  (${deliveryRate}%)`);
    statRow('Undeliverable',  failed);

    y += 3;
    sectionHeader('Engagement');
    statRow('Link Clicks',        `${linkClickCount}  (${clickRate}%)`);
    statRow('Unsubscribe Clicks', `${usClickCount}  (${unsubRate}%)`);

    // ── Pie chart ──
    y += 6;
    const canvas = document.createElement('canvas');
    canvas.width = 300; canvas.height = 300;
    document.body.appendChild(canvas);
    new ChartJS(canvas.getContext('2d'), {
      type: 'pie',
      data: {
        labels: ['Delivered', 'Undeliverable'],
        datasets: [{ data: [delivered, failed], backgroundColor: ['#ADD8E6', '#FFB6C1'] }],
      },
      options: { animation: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 14 } } } } },
    });
    await new Promise(r => setTimeout(r, 500));
    doc.addImage(canvas.toDataURL('image/png'), 'PNG', 30, y, 90, 90);
    canvas.remove();

    // ── AI Insights ──
    if (aiInsights) {
      y += 96;
      sectionHeader('AI Insights');
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.splitTextToSize(aiInsights, 175).forEach(line => {
        if (y > 275) { doc.addPage(); y = 15; }
        doc.text(line, 20, y); y += 5;
      });
    }

    // ── Footer ──
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(186, 186, 186);
    const reportDate = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    doc.text(`Report Date: ${reportDate}`, 105, 281, { align: 'center' });
    doc.text('Generated by VYBRNT SMS', 105, 287, { align: 'center' });

    doc.save(`campaign-${campaignData?.name || 'report'}.pdf`);
  };

  // ── Export: CSV ────────────────────────────────────────────────────
  // The table only holds the current page, so page through the SMS log API
  // to export every contact in the campaign.
  const fetchAllLogRows = async () => {
    const all = [];
    let currentPage = 1;
    let lastPage = 1;
    do {
      const res = await axios.get(
        Constants.getApiPathWithConfigId(Constants.campaignsmslogsApiPath) +
          `/${id}?per_page=500&page=${currentPage}&order_by=id&order=desc`,
        getHeaders()
      );
      all.push(...(res.data.data || []));
      lastPage = res.data.meta?.last_page || 1;
      currentPage++;
    } while (currentPage <= lastPage);
    return all;
  };

  const clickCount = raw => {
    try { return JSON.parse(raw)?.length || 0; } catch { return 0; }
  };

  const downloadCSV = async () => {
    setCsvLoading(true);
    let allRows;
    try {
      allRows = await fetchAllLogRows();
    } catch (err) {
      console.error(err);
      toastMaker.makeToast("Failed to download CSV.", { variant: "error" });
      setCsvLoading(false);
      return;
    }
    setCsvLoading(false);

    const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Campaign Performance Report'],
      [''],
      ['Campaign', campaignData?.name],
      ['Sender ID', campaignData?.sender_id],
      ['Date', new Date(campaignData?.created_at).toLocaleString('en-GB')],
      ['List', campaignData?.list_ac_names],
      ['Segment', campaignData?.segment_ac_names],
      ['Status', statuses[campaignData?.status]],
      [''],
      ['DELIVERY SUMMARY'],
      ['Total Sent', total],
      ['Delivered', delivered],
      ['Delivery Rate', `${deliveryRate}%`],
      ['Undeliverable', failed],
      [''],
      ['ENGAGEMENT'],
      ['Link Clicks', linkClickCount],
      ['Click Rate', `${clickRate}%`],
      ['Unsubscribe Clicks', usClickCount],
      ['Unsub Rate', `${unsubRate}%`],
      [''],
      ['CONTACT LOG'],
      ['Phone Number', 'Status', 'Link Clicks', 'Unsub Clicks', 'Message'],
      ...allRows.map(r => [
        r.phone_number,
        r.status === 2 ? 'Undeliverable' : 'Delivered',
        clickCount(r.link_click_data),
        clickCount(r.unsub_link_click_data),
        getSms(r),
      ]),
    ];

    // BOM so Excel opens the file as UTF-8 (emoji / non-Latin message text).
    const csv  = '﻿' + rows.map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `campaign-${campaignData?.name || 'report'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Copy report ────────────────────────────────────────────────────
  const copyReport = () => {
    if (!clipboard.isSupported()) {
      toastMaker.makeToast("Copy not supported by this browser.", { variant: "error" });
      return;
    }
    const fmt      = n => Number(n || 0).toLocaleString();
    const divider  = '-'.repeat(44);
    const pad      = (label, value) => `  ${label.padEnd(22)}${value}`;
    const reportDate = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

    const narrative = total > 0
      ? `Campaign "${campaignData?.name}" sent ${fmt(total)} messages. ${fmt(delivered)} were successfully delivered - a ${deliveryRate}% delivery rate. ${fmt(linkClickCount)} recipient${linkClickCount !== 1 ? 's' : ''} clicked a link (${clickRate}% click rate)${usClickCount > 0 ? `, and ${fmt(usClickCount)} unsubscribed` : ''}.`
      : `No messages were sent for this campaign.`;

    const text = [
      `Campaign Performance Report`,
      `Report Date: ${reportDate}`,
      ``,
      narrative,
      ``,
      divider,
      `CAMPAIGN DETAILS`,
      pad('Campaign',  campaignData?.name),
      pad('Sender ID', campaignData?.sender_id),
      pad('Date',      new Date(campaignData?.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })),
      pad('List',      campaignData?.list_ac_names),
      pad('Segment',   campaignData?.segment_ac_names),
      ``,
      `DELIVERY`,
      pad('Total Sent',    fmt(total)),
      pad('Delivered',     `${fmt(delivered)}  (${deliveryRate}%)`),
      pad('Undeliverable', fmt(failed)),
      ``,
      `ENGAGEMENT`,
      pad('Link Clicks',        `${fmt(linkClickCount)}  (${clickRate}%)`),
      pad('Unsubscribe Clicks', `${fmt(usClickCount)}  (${unsubRate}%)`),
      divider,
      `Generated by VYBRNT SMS`,
    ].join('\n');

    clipboard.copy(text);
  };

  // ── AI Insights ────────────────────────────────────────────────────
  const fetchAiInsights = () => {
    setAiLoading(true);
    setAiInsights('');
    axios
      .post(
        Constants.getApiPathWithConfigId(Constants.postCampaignInsightsApiPath),
        {
          campaign_name:  campaignData?.name,
          total:          total,
          delivered:      delivered,
          failed:         failed,
          delivery_rate:  deliveryRate,
          link_clicks:    linkClickCount,
          click_rate:     clickRate,
          unsub_clicks:   usClickCount,
          unsub_rate:     unsubRate,
        },
        getHeaders()
      )
      .then(res => {
        setAiInsights(res.data.data?.insights || res.data.data || '');
        setAiLoading(false);
      })
      .catch(err => {
        console.error(err);
        setAiLoading(false);
        toastMaker.makeToast("AI insights unavailable right now.", { variant: "warning" });
      });
  };

  if (!serverData || !campaignData) return <LoadingProgress />;

  return (
    <>
      {/* ── Header ── */}
      <div className="panal-viewport">
        <Header name={"Campaign Report"} />
      </div>

      {/* ── Action bar ── */}
      <div className="row mb-3 ml-2" style={{ alignItems: 'center' }}>
        <div className="col-5">
          <span style={{ fontWeight: '600', fontSize: '15px', color: '#122643' }}>
            {campaignData?.name}
          </span>
          <span style={{ marginLeft: '10px', fontSize: '12px', color: '#6B6C7E' }}>
            {statuses[campaignData?.status]}
          </span>
        </div>
        <div className="col-7 d-flex justify-content-end" style={{ gap: '6px', flexWrap: 'wrap' }}>
          <p className="c-button c-button--secondary text-uppercase text-light m-1" style={{ fontSize: '11px' }}
            onClick={() => Constants.navigate(Constants.campaignhistory)}>
            ← Back
          </p>
          <p className="c-button c-button--secondary text-uppercase text-light m-1" style={{ fontSize: '11px' }}
            onClick={downloadPDF}>
            Download PDF
          </p>
          <p className="c-button c-button--secondary text-uppercase text-light m-1"
            style={{ fontSize: '11px', opacity: csvLoading ? 0.5 : 1, cursor: csvLoading ? 'default' : 'pointer' }}
            onClick={() => { if (!csvLoading) downloadCSV(); }}>
            {csvLoading ? 'Preparing…' : 'Download CSV'}
          </p>
          <p className="c-button c-button--secondary text-uppercase text-light m-1" style={{ fontSize: '11px' }}
            onClick={copyReport}>
            Copy Report
          </p>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div style={{ display: 'flex', gap: '10px', padding: '0 0 16px 0', flexWrap: 'wrap' }}>
        <StatCard label="Total Sent"    value={total}           color="#122643" />
        <StatCard label="Delivered"     value={delivered}       color="#28a745" />
        <StatCard label="Delivery Rate" value={`${deliveryRate}%`} color="#28a745" />
        <StatCard label="Undeliverable" value={failed}          color="#dc3545" />
        <StatCard label="Link Clicks"   value={linkClickCount}  color="#45A9F0" />
        <StatCard label="Click Rate"    value={`${clickRate}%`} color="#45A9F0" />
        <StatCard label="Unsubscribes"  value={usClickCount}    color="#e67e22"
          sub={`${unsubRate}% unsub rate`} />
      </div>


      {/* ── Main body: details + chart | log table ── */}
      <div className="d-flex flex-row" style={{ gap: '12px', alignItems: 'flex-start' }}>

        {/* Left: campaign meta + pie chart */}
        <Paper style={{ width: '280px', flexShrink: 0, padding: '14px' }}>
          {[
            ['Sender ID',  campaignData?.sender_id],
            ['Date',       new Date(campaignData?.created_at).toLocaleString('en-GB', { day:'numeric', month:'numeric', year:'numeric', hour:'numeric', minute:'2-digit', hour12:true })],
            ['List',       campaignData?.list_ac_names],
            ['Segment',    campaignData?.segment_ac_names],
            ['Type',       campaignData?.type?.toUpperCase()],
            ['Status',     statuses[campaignData?.status]],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
              <span style={{ color: '#6B6C7E', flexShrink: 0, marginRight: '8px' }}>{label}</span>
              <span style={{ fontWeight: '600', color: '#272833', textAlign: 'right' }}>{value}</span>
            </div>
          ))}
          <div style={{ marginTop: '16px' }}>
            <Pie data={chartData} options={chartOptions} />
          </div>
        </Paper>

        {/* Right: SMS log table */}
        <Paper className="pb-2 flex-grow-1" style={{ overflow: 'hidden' }}>
          {statuses[campaignData?.status] === "Completed" ? (
            <TableContainer>
              <Table aria-label="sms log table">
                <TableHead>
                  <TableRow id="table-head_rw">
                    <StyledTableCell className="table-hd">Sent Message</StyledTableCell>
                    <StyledTableCell className="table-hd">Sender ID</StyledTableCell>
                    <StyledTableCell className="table-hd">Contact</StyledTableCell>
                    <StyledTableCell className="table-hd">Status</StyledTableCell>
                    <StyledTableCell className="table-hd">Clicks</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serverData?.length > 0 ? serverData.map(r => (
                    <StyledTableRow key={r.id}>
                      <StyledTableCell>
                        <div style={{
                          maxWidth: '340px',
                          background: '#F4F7FF',
                          border: '1px solid #D6E4FF',
                          borderRadius: '10px',
                          padding: '8px 12px',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          color: '#272833',
                          wordBreak: 'break-word',
                          fontWeight: 'normal',
                        }}>
                          <span style={{
                            display: 'inline-block',
                            background: r.type === 'mms' ? '#45A9F0' : '#6B6C7E',
                            color: '#fff',
                            fontSize: '10px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            padding: '1px 7px',
                            borderRadius: '10px',
                            marginBottom: '6px',
                          }}>
                            {r.type === 'mms' ? 'MMS' : 'SMS'}
                          </span>
                          {r.type === 'mms' && r.subject && (
                            <div style={{ fontWeight: 700, marginTop: '4px', marginBottom: '4px' }}>
                              Subject: {r.subject}
                            </div>
                          )}
                          {r.type === 'mms' && r.url && (
                            <div style={{ marginBottom: '6px' }}>
                              <img
                                src={r.url}
                                alt="MMS media"
                                onClick={() => setMediaUrl(r.url)}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '160px',
                                  display: 'block',
                                  borderRadius: '6px',
                                  cursor: 'zoom-in',
                                }}
                              />
                            </div>
                          )}
                          <div><span>{getSms(r)}</span></div>
                        </div>
                      </StyledTableCell>
                      <StyledTableCell>
                        <span style={{ fontWeight: 'normal', fontSize: '13px' }}>{campaignData?.sender_id}</span>
                      </StyledTableCell>
                      <StyledTableCell>
                        <span style={{ fontWeight: 'normal', fontSize: '13px' }}>{r.phone_number}</span>
                      </StyledTableCell>
                      <StyledTableCell>
                        <span style={{ fontWeight: '600', fontSize: '13px', color: r.status === 2 ? '#dc3545' : '#28a745' }}>
                          {r.status === 2 ? 'Undeliverable' : 'Delivered'}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell>
                        <span style={{ fontSize: '13px' }}>
                          <a href="#" className="text-success" title="Link clicks"
                            onClick={e => { e.preventDefault(); setDialogTitle('Link Clicks'); setDialogData(r?.link_click_data); setDialogOpen(true); }}>
                            {JSON.parse(r?.link_click_data)?.length || 0}
                          </a>
                          {' / '}
                          <a href="#" className="text-danger" title="Unsubscribe clicks"
                            onClick={e => { e.preventDefault(); setDialogTitle('Unsubscribe Clicks'); setDialogData(r?.unsub_link_click_data); setDialogOpen(true); }}>
                            {JSON.parse(r?.unsub_link_click_data)?.length || 0}
                          </a>
                        </span>
                      </StyledTableCell>
                    </StyledTableRow>
                  )) : (
                    <TableRow>
                      <StyledTableCell colSpan={5} style={{ textAlign: 'center', fontWeight: 'normal', color: '#BABABA' }}>
                        No data found.
                      </StyledTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div style={{ padding: '20px', color: '#6B6C7E', fontSize: '13px' }}>
              The detailed log will appear once the campaign completes.
            </div>
          )}

          {pageCount > 0 && (
            <div className={`${classes.root} mt-3 mb-3 d-flex justify-content-end`}>
              <Pagination count={pageCount} page={page} onChange={(e, v) => setPage(v)} variant="outlined" shape="rounded" />
            </div>
          )}
        </Paper>
      </div>

      {/* ── Link clicks dialog ── */}
      <Dialog onClose={() => setDialogOpen(false)} open={dialogOpen}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {dialogData ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow id="table-head_rw">
                    <StyledTableCell className="table-hd">Link</StyledTableCell>
                    <StyledTableCell className="table-hd">Date</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {JSON.parse(dialogData)?.map((r, i) => (
                    <TableRow key={i}>
                      <StyledTableCell>
                        <a href={r?.eventDetails?.link} target="_blank" rel="noreferrer" title={r?.eventDetails?.link} style={{ fontSize: '13px', fontWeight: 'normal' }}>
                          {(r?.eventDetails?.link || '').substr(0, 50)}…
                        </a>
                      </StyledTableCell>
                      <StyledTableCell style={{ fontWeight: 'normal', fontSize: '13px' }}>
                        {new Date(r?.timestamp * 1000).toLocaleDateString()}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <span style={{ color: '#BABABA', fontSize: '13px' }}>No click data found.</span>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 24px' }}>
          <span className="c-button text-uppercase" onClick={() => setDialogOpen(false)}>Close</span>
        </DialogActions>
      </Dialog>

      {/* ── Media popup dialog ── */}
      <Dialog onClose={() => setMediaUrl('')} open={!!mediaUrl} maxWidth="md">
        <DialogTitle>MMS Media</DialogTitle>
        <DialogContent>
          {mediaUrl && (
            <img
              src={mediaUrl}
              alt="MMS media"
              style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
            />
          )}
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 24px' }}>
          <span className="c-button text-uppercase" onClick={() => setMediaUrl('')}>Close</span>
        </DialogActions>
      </Dialog>
    </>
  );
}
